import CompetitorType from '@app/model/company_hotel/CompetitorType';
import type PricingStrategyCompetitorImportance from '@app/model/pricing-automation/PricingStrategyCompetitorImportance';

export function pricingHotelCompetitors<T extends { type: CompetitorType }>(competitors: T[]): T[] {
	return competitors.filter(competitor => competitor.type === CompetitorType.HOTEL);
}

type CompetitorReference = {
	id: number;
};

export function competitorImportanceDefaultsMutationKey(companyHotelId: number) {
	return ['pricing-strategy-competitor-importance-defaults', companyHotelId] as const;
}

export function normalizeCompetitorImportances(
	competitors: CompetitorReference[],
	competitorImportances: PricingStrategyCompetitorImportance[] = [],
): PricingStrategyCompetitorImportance[] {
	const importancesByCompetitorId = new Map(
		competitorImportances.map(importance => [importance.competitorId, importance]),
	);

	return competitors.map(competitor => importancesByCompetitorId.get(competitor.id) ?? {
		competitorId: competitor.id,
		importance: '3',
	});
}

export function findMissingCompetitorImportances(
	competitors: CompetitorReference[],
	competitorImportances: PricingStrategyCompetitorImportance[] = [],
): PricingStrategyCompetitorImportance[] {
	const persistedCompetitorIds = new Set(
		competitorImportances.map(importance => importance.competitorId),
	);

	return competitors
		.filter(competitor => !persistedCompetitorIds.has(competitor.id))
		.map(competitor => ({
			competitorId: competitor.id,
			importance: '3',
		}));
}

// The word of each importance value, from 0 up. The pricer uses the value as the weight of the competitor price.
export const IMPORTANCE_WORDS = ['Not used', 'Very low', 'Low', 'Normal', 'High', 'Very high'];

export function allImportancesZero(importances: number[]): boolean {
	return importances.every(importance => importance === 0);
}

// The part of each competitor in the market price: its importance divided by the sum of all importances.
// A competitor at 0, or a page with no importance above 0, has no share.
export function importanceShare(importance: number, importances: number[]): number | null {
	const total = importances.reduce((sum, value) => sum + value, 0);
	return importance > 0 && total > 0 ? importance / total * 100 : null;
}

export function formatImportanceShare(share: number | null): string {
	return share === null ? '—' : `${share.toFixed(1)}%`;
}
